import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { 
  Users, 
  Building2, 
  Truck, 
  User, 
  Search, 
  Plus,
  Edit,
  Car,
  Phone,
  Mail,
  MapPin,
  Calendar,
  ChevronRight,
  RefreshCw,
  Filter,
  X,
  Clock,
  FileText,
  Download,
  Upload,
  FileUp,
  FileDown,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminCustomers = () => {
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({ total: 0, private: 0, business: 0, fleet: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [customerBookings, setCustomerBookings] = useState([]);
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [editForm, setEditForm] = useState({});
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importMode, setImportMode] = useState('update');
  const [importing, setImporting] = useState(false);

  const fetchCustomers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('customer_type', typeFilter);
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Feil ved henting av kunder:', error);
    }
  }, [token, typeFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Feil ved henting av statistikk:', error);
    }
  }, [token]);

  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      fetchCustomers();
      return;
    }
    try {
      const response = await axios.get(`${API}/customers/search?q=${encodeURIComponent(query)}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Feil ved søk:', error);
    }
  }, [token, fetchCustomers]);

  const fetchCustomerDetails = async (customerId) => {
    try {
      const [bookingsRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/customers/${customerId}/bookings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/customers/${customerId}/vehicles`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCustomerBookings(bookingsRes.data);
      setCustomerVehicles(vehiclesRes.data);
    } catch (error) {
      console.error('Feil ved henting av kundedetaljer:', error);
    }
  };

  // Export customers to CSV
  const exportCustomers = async () => {
    try {
      const params = typeFilter !== 'all' ? `?customer_type=${typeFilter}` : '';
      const response = await axios.get(`${API}/customers/export/csv${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kunder_${new Date().toISOString().split('T')[0]}.tsv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Feil ved eksport:', error);
      alert('Kunne ikke eksportere kunder');
    }
  };

  // Handle CSV file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      const lines = content.split('\n');
      const headers = lines[0].split(/[\t,]/);
      
      const preview = [];
      for (let i = 1; i < Math.min(6, lines.length); i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(/[\t,]/);
          const row = {};
          headers.forEach((header, idx) => {
            row[header.trim()] = values[idx]?.trim() || '';
          });
          preview.push(row);
        }
      }
      setImportPreview(preview);
    };
    reader.readAsText(file);
  };

  // Handle customer import
  const handleImport = async () => {
    if (!importFile) return;
    
    setImporting(true);
    
    try {
      const content = await importFile.text();
      
      const response = await axios.post(
        `${API}/customers/import/csv?mode=${importMode}`,
        content,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'text/plain'
          }
        }
      );
      
      const result = response.data;
      
      let message = `Import fullført!\n\n`;
      message += `Totalt rader: ${result.total}\n`;
      if (result.created > 0) message += `Nye kunder: ${result.created}\n`;
      if (result.updated > 0) message += `Oppdaterte: ${result.updated}\n`;
      if (result.skipped > 0) message += `Hoppet over: ${result.skipped}\n`;
      if (result.failed > 0) {
        message += `Feilet: ${result.failed}\n`;
        if (result.errors.length > 0) {
          message += `\nFeil:\n${result.errors.slice(0, 5).join('\n')}`;
          if (result.errors.length > 5) message += `\n... og ${result.errors.length - 5} flere feil`;
        }
      }
      
      alert(message);
      
      setShowImportModal(false);
      setImportFile(null);
      setImportPreview([]);
      fetchCustomers();
      fetchStats();
      
    } catch (error) {
      console.error('Import feilet:', error);
      alert('Kunne ikke importere: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  // Download CSV template
  const downloadTemplate = () => {
    const template = `name\temail\tphone\tcompany_name\torg_number\taddress\tpostal_code\tcity\tcustomer_type\tnotes
Ola Nordmann\tola@example.com\t99887766\t\t\tStorgata 1\t0101\tOslo\tprivate\tVIP-kunde
Bedriften AS\tkontakt@bedriften.no\t22334455\tBedriften AS\t123456789\tIndustriveien 5\t3000\tDrammen\tbusiness\tFast avtale`;
    
    const blob = new Blob([template], { type: 'text/tab-separated-values' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kunde_mal.tsv';
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCustomers(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCustomers, fetchStats]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchCustomers(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchCustomers]);

  const openCustomerDetail = async (customer) => {
    setSelectedCustomer(customer);
    setShowDetailModal(true);
    await fetchCustomerDetails(customer.id);
  };

  const openEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setEditForm({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      company_name: customer.company_name || '',
      org_number: customer.org_number || '',
      address: customer.address || '',
      postal_code: customer.postal_code || '',
      city: customer.city || '',
      customer_type: customer.customer_type || 'auto',
      notes: customer.notes || ''
    });
    setShowEditModal(true);
  };

  const saveCustomer = async () => {
    try {
      await axios.put(`${API}/customers/${selectedCustomer.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEditModal(false);
      fetchCustomers();
      fetchStats();
    } catch (error) {
      console.error('Feil ved lagring:', error);
      alert('Kunne ikke lagre kunde: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getCustomerTypeIcon = (customer) => {
    const type = customer.customer_type !== 'auto' ? customer.customer_type : customer.customer_type_auto;
    switch (type) {
      case 'business': return <Building2 className="w-5 h-5 text-blue-500" />;
      case 'fleet': return <Truck className="w-5 h-5 text-purple-500" />;
      default: return <User className="w-5 h-5 text-green-500" />;
    }
  };

  const getCustomerTypeBadge = (customer) => {
    const type = customer.customer_type !== 'auto' ? customer.customer_type : customer.customer_type_auto;
    const isManual = customer.customer_type !== 'auto';
    switch (type) {
      case 'business':
        return <Badge className="bg-blue-600 text-white">{isManual ? 'Bedrift' : 'Bedrift (auto)'}</Badge>;
      case 'fleet':
        return <Badge className="bg-purple-600 text-white">{isManual ? 'Flåte' : 'Flåte (auto)'}</Badge>;
      default:
        return <Badge className="bg-green-600 text-white">{isManual ? 'Privat' : 'Privat (auto)'}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nb-NO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Kunder</h1>
          <p className="text-gray-400 mt-1">Administrer kunder og se bookinghistorikk</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={exportCustomers}
            variant="outline" 
            className="border-green-600 text-green-400 hover:bg-green-900/30"
            data-testid="export-customers-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Eksporter CSV
          </Button>
          <Button 
            onClick={() => setShowImportModal(true)}
            variant="outline" 
            className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
            data-testid="import-customers-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importer CSV
          </Button>
          <Button 
            onClick={() => {
              fetchCustomers();
              fetchStats();
            }}
            variant="outline" 
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Oppdater
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className={`bg-gray-800/50 border-gray-700 cursor-pointer transition-all ${typeFilter === 'all' ? 'ring-2 ring-red-500' : 'hover:bg-gray-800'}`}
          onClick={() => setTypeFilter('all')}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-gray-700 rounded-lg">
              <Users className="w-6 h-6 text-gray-300" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-sm text-gray-400">Totalt</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`bg-gray-800/50 border-gray-700 cursor-pointer transition-all ${typeFilter === 'private' ? 'ring-2 ring-green-500' : 'hover:bg-gray-800'}`}
          onClick={() => setTypeFilter('private')}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-900/50 rounded-lg">
              <User className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.private}</p>
              <p className="text-sm text-gray-400">Privat</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`bg-gray-800/50 border-gray-700 cursor-pointer transition-all ${typeFilter === 'business' ? 'ring-2 ring-blue-500' : 'hover:bg-gray-800'}`}
          onClick={() => setTypeFilter('business')}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-900/50 rounded-lg">
              <Building2 className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.business}</p>
              <p className="text-sm text-gray-400">Bedrift</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`bg-gray-800/50 border-gray-700 cursor-pointer transition-all ${typeFilter === 'fleet' ? 'ring-2 ring-purple-500' : 'hover:bg-gray-800'}`}
          onClick={() => setTypeFilter('fleet')}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-purple-900/50 rounded-lg">
              <Truck className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.fleet}</p>
              <p className="text-sm text-gray-400">Flåte</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Søk på navn, e-post, telefon eller firma..."
                className="pl-10 bg-gray-900 border-gray-600 text-white placeholder:text-gray-500"
                data-testid="customer-search-input"
              />
            </div>
            {typeFilter !== 'all' && (
              <Button
                onClick={() => setTypeFilter('all')}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <X className="w-4 h-4 mr-2" />
                Fjern filter
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Customer List */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5" />
            Kundeliste ({customers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {customers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400 text-lg">Ingen kunder funnet</p>
              {searchQuery && (
                <p className="text-gray-500 mt-2">Prøv et annet søkeord</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg hover:bg-gray-900 transition-all cursor-pointer group"
                  onClick={() => openCustomerDetail(customer)}
                  data-testid={`customer-row-${customer.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-gray-800 rounded-lg">
                      {getCustomerTypeIcon(customer)}
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-white">{customer.name}</span>
                        {getCustomerTypeBadge(customer)}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {customer.phone}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {customer.email}
                        </span>
                        {customer.company_name && (
                          <span className="flex items-center gap-1 text-blue-400">
                            <Building2 className="w-3 h-3" />
                            {customer.company_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <p className="text-gray-400">{customer.total_bookings || 0} bookinger</p>
                      {customer.last_booking_date && (
                        <p className="text-gray-500">Siste: {formatDate(customer.last_booking_date)}</p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-gray-400 transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white flex items-center gap-3">
              {selectedCustomer && getCustomerTypeIcon(selectedCustomer)}
              {selectedCustomer?.name}
            </DialogTitle>
          </DialogHeader>

          {selectedCustomer && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-gray-800/50 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Kontaktinformasjon</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-3 text-gray-300">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <a href={`tel:${selectedCustomer.phone}`} className="hover:text-white">
                        {selectedCustomer.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-3 text-gray-300">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <a href={`mailto:${selectedCustomer.email}`} className="hover:text-white">
                        {selectedCustomer.email}
                      </a>
                    </div>
                    {(selectedCustomer.address || selectedCustomer.city) && (
                      <div className="flex items-center gap-3 text-gray-300">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span>
                          {selectedCustomer.address && `${selectedCustomer.address}, `}
                          {selectedCustomer.postal_code} {selectedCustomer.city}
                        </span>
                      </div>
                    )}
                    {selectedCustomer.company_name && (
                      <div className="flex items-center gap-3 text-gray-300">
                        <Building2 className="w-4 h-4 text-gray-500" />
                        <span>{selectedCustomer.company_name}</span>
                        {selectedCustomer.org_number && (
                          <span className="text-gray-500">({selectedCustomer.org_number})</span>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-gray-800/50 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Statistikk</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Type:</span>
                      {getCustomerTypeBadge(selectedCustomer)}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Totalt bookinger:</span>
                      <span className="text-white font-semibold">{selectedCustomer.total_bookings || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Kjøretøy:</span>
                      <span className="text-white font-semibold">{customerVehicles.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Kunde siden:</span>
                      <span className="text-white">{formatDate(selectedCustomer.created_at)}</span>
                    </div>
                    {selectedCustomer.last_booking_date && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Siste booking:</span>
                        <span className="text-white">{formatDate(selectedCustomer.last_booking_date)}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Vehicles */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Car className="w-5 h-5" />
                    Kjøretøy ({customerVehicles.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {customerVehicles.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Ingen kjøretøy registrert</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {customerVehicles.map((vehicle) => (
                        <div key={vehicle.id} className="p-3 bg-gray-900/50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-white">{vehicle.reg_number}</span>
                            <Badge variant="outline" className="text-gray-400 border-gray-600">
                              {vehicle.vehicle_type === 'car' ? 'Personbil' :
                               vehicle.vehicle_type === 'van' ? 'Varebil' :
                               vehicle.vehicle_type === 'truck' ? 'Lastebil' :
                               vehicle.vehicle_type}
                            </Badge>
                          </div>
                          {(vehicle.make || vehicle.model) && (
                            <p className="text-gray-400 text-sm mt-1">
                              {vehicle.make} {vehicle.model} {vehicle.year && `(${vehicle.year})`}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Booking History */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Bookinghistorikk ({customerBookings.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {customerBookings.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Ingen bookinger registrert</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {customerBookings.map((booking) => (
                        <div key={booking.id} className="p-3 bg-gray-900/50 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="text-center">
                              <p className="text-white font-semibold">{formatDate(booking.date)}</p>
                              <p className="text-gray-500 text-sm">{booking.time_slot}</p>
                            </div>
                            <div>
                              <p className="text-gray-300">{booking.service_name || 'Tjeneste'}</p>
                              {booking.reg_number && (
                                <p className="text-gray-500 text-sm">{booking.reg_number}</p>
                              )}
                            </div>
                          </div>
                          <Badge className={
                            booking.status === 'completed' ? 'bg-blue-600' :
                            booking.status === 'confirmed' ? 'bg-green-600' :
                            booking.status === 'cancelled' ? 'bg-red-600' :
                            'bg-yellow-600'
                          }>
                            {booking.status === 'completed' ? 'Fullført' :
                             booking.status === 'confirmed' ? 'Bekreftet' :
                             booking.status === 'cancelled' ? 'Kansellert' :
                             booking.status === 'pending' ? 'Venter' :
                             booking.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notes */}
              {selectedCustomer.notes && (
                <Card className="bg-gray-800/50 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Notater
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 whitespace-pre-wrap">{selectedCustomer.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              onClick={() => setShowDetailModal(false)}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Lukk
            </Button>
            <Button
              onClick={() => {
                setShowDetailModal(false);
                openEditCustomer(selectedCustomer);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Edit className="w-4 h-4 mr-2" />
              Rediger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Rediger kunde</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Navn *</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Telefon *</Label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">E-post *</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Firmanavn</Label>
                <Input
                  value={editForm.company_name}
                  onChange={(e) => setEditForm({...editForm, company_name: e.target.value})}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="For bedriftskunder"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Org.nummer</Label>
                <Input
                  value={editForm.org_number}
                  onChange={(e) => setEditForm({...editForm, org_number: e.target.value})}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="9 siffer"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Adresse</Label>
              <Input
                value={editForm.address}
                onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Postnummer</Label>
                <Input
                  value={editForm.postal_code}
                  onChange={(e) => setEditForm({...editForm, postal_code: e.target.value})}
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Sted</Label>
                <Input
                  value={editForm.city}
                  onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Kundetype</Label>
              <select
                value={editForm.customer_type}
                onChange={(e) => setEditForm({...editForm, customer_type: e.target.value})}
                className="w-full p-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
              >
                <option value="auto">Automatisk (basert på e-post/org.nr)</option>
                <option value="private">Privat</option>
                <option value="business">Bedrift</option>
                <option value="fleet">Flåte</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Notater</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                className="bg-gray-800 border-gray-600 text-white min-h-[100px]"
                placeholder="Interne notater om kunden..."
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              onClick={() => setShowEditModal(false)}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Avbryt
            </Button>
            <Button
              onClick={saveCustomer}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Lagre endringer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={(open) => {
        setShowImportModal(open);
        if (!open) {
          setImportFile(null);
          setImportPreview([]);
        }
      }}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white flex items-center gap-3">
              <Upload className="w-6 h-6 text-blue-400" />
              Importer kunder fra CSV
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Mode Selection */}
            <div className="space-y-4">
              <Label className="text-lg font-medium text-gray-200">Importmodus</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  onClick={() => setImportMode('update')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    importMode === 'update' 
                      ? 'border-blue-500 bg-blue-900/30' 
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={importMode === 'update'}
                      onChange={() => setImportMode('update')}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-semibold text-white">Oppdater</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Legger til nye kunder. Oppdaterer eksisterende hvis e-post matcher.
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  onClick={() => setImportMode('skip')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    importMode === 'skip' 
                      ? 'border-green-500 bg-green-900/30' 
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={importMode === 'skip'}
                      onChange={() => setImportMode('skip')}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-semibold text-white">Kun nye</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Legger kun til nye kunder. Hopper over hvis e-post allerede finnes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-lg font-medium text-gray-200">Velg CSV-fil</Label>
                <Button
                  onClick={downloadTemplate}
                  variant="outline"
                  size="sm"
                  className="border-blue-500 text-blue-400 hover:bg-blue-900/30"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Last ned mal
                </Button>
              </div>
              
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  accept=".csv,.tsv,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="customer-csv-input"
                  data-testid="customer-csv-input"
                />
                <label htmlFor="customer-csv-input" className="cursor-pointer">
                  <FileUp className="w-12 h-12 mx-auto text-gray-500 mb-3" />
                  <p className="text-gray-300">
                    {importFile ? importFile.name : 'Klikk for å velge fil'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Støtter CSV og TSV-filer
                  </p>
                </label>
              </div>
            </div>

            {/* Expected Format */}
            <div className="bg-gray-800/50 rounded-lg p-4">
              <p className="font-medium text-gray-300 mb-2">Forventet format (kolonner):</p>
              <code className="text-xs bg-gray-900 p-2 rounded block overflow-x-auto text-gray-400">
                name, email, phone, company_name, org_number, address, postal_code, city, customer_type, notes
              </code>
              <p className="text-xs text-gray-500 mt-2">
                * name, email og phone er obligatoriske<br/>
                * customer_type kan være: private, business, fleet (eller tom for auto)
              </p>
            </div>

            {/* Preview */}
            {importPreview.length > 0 && (
              <div className="space-y-3">
                <Label className="text-lg font-medium text-gray-200">Forhåndsvisning</Label>
                <div className="overflow-x-auto border border-gray-700 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="p-2 text-left text-gray-400">Navn</th>
                        <th className="p-2 text-left text-gray-400">E-post</th>
                        <th className="p-2 text-left text-gray-400">Telefon</th>
                        <th className="p-2 text-left text-gray-400">Firma</th>
                        <th className="p-2 text-left text-gray-400">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map((row, idx) => (
                        <tr key={idx} className="border-t border-gray-700">
                          <td className="p-2 text-gray-300">{row.name}</td>
                          <td className="p-2 text-gray-300">{row.email}</td>
                          <td className="p-2 text-gray-300">{row.phone}</td>
                          <td className="p-2 text-gray-300">{row.company_name || '-'}</td>
                          <td className="p-2 text-gray-300">{row.customer_type || 'auto'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
                setImportPreview([]);
              }}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleImport}
              disabled={!importFile || importing}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
              data-testid="confirm-customer-import-btn"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importerer...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Importer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomers;
